/**
 * Shared internals for the chart primitives (not exported from index.ts).
 *
 * (colours, patterns fills, niceTicks, useWidth live in chartUtils.ts)
 * - PatternDef: series 0 is solid, 1 striped, 2 dotted, 3 hatched, so no series relies on colour alone.
 * - ChartState: loading skeleton / empty state / children.
 * - DataTable: visually hidden table read by screen readers (the drawn chart is aria-hidden).
 * - Swatch, Legend, Tooltip: legend key with the series pattern; tooltip box shared by the charts.
 */
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/Skeleton';
import { SURFACE, colorOf } from './chartUtils';


/** SVG pattern for series i >= 1 (id `${uid}-${i}`); render inside <defs>. */
export const PatternDef = ({ uid, i }: { uid: string; i: number }) => {
    const c = colorOf(i);
    const kind = i % 4;
    if (kind === 0) return null;
    return (
        <pattern id={`${uid}-${i}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform={kind === 1 ? 'rotate(45)' : undefined}>
            <rect width="6" height="6" fill={c} />
            {kind === 1 && <rect width="2.5" height="6" fill={SURFACE} />}
            {kind === 2 && <circle cx="3" cy="3" r="1.3" fill={SURFACE} />}
            {kind === 3 && <path d="M0 3H6M3 0V6" stroke={SURFACE} strokeWidth="1.2" />}
        </pattern>
    );
};


export const Swatch = ({ uid, i }: { uid: string; i: number }) => (
    <svg width="14" height="14" aria-hidden="true" focusable="false" className="shrink-0 rounded-sm">
        <defs>
            <PatternDef uid={`${uid}-sw`} i={i} />
        </defs>
        <rect width="14" height="14" fill={i % 4 === 0 ? colorOf(0) : `url(#${uid}-sw-${i})`} />
    </svg>
);

/** Legend: the swatch repeats the series pattern. */
export const Legend = ({ uid, entries }: { uid: string; entries: { key: string; label: string; text?: string }[] }) => (
    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-small text-muted" aria-hidden="true">
        {entries.map((e, i) => (
            <li key={e.key} className="flex items-center gap-2">
                <Swatch uid={uid} i={i} />
                <span>{e.label}</span>
                {e.text !== undefined && <span className="tabular-nums text-strong">{e.text}</span>}
            </li>
        ))}
    </ul>
);


export const DataTable = ({ caption, headers, rows }: { caption: string; headers: string[]; rows: string[][] }) => (
    <div className="sr-only">
        <table>
            <caption>{caption}</caption>
            <thead>
                <tr>
                    {headers.map((h, i) => (
                        <th key={i} scope="col">
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, ri) => (
                    <tr key={ri}>
                        {r.map((cell, ci) =>
                            ci === 0 ? (
                                <th key={ci} scope="row">
                                    {cell}
                                </th>
                            ) : (
                                <td key={ci}>{cell}</td>
                            ),
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

interface ChartStateProps {
    loading?: boolean | undefined;
    empty: boolean;
    height: number;
    className?: string | undefined;
    children: ReactNode;
}

/** Loading skeleton, then the empty message, then the chart. */
export const ChartState = ({ loading, empty, height, className, children }: ChartStateProps) => {
    const { t } = useTranslation('charts');
    if (loading) {
        return (
            <div role="status" aria-busy="true" className={className} style={{ height }}>
                <span className="sr-only">{t('loading')}</span>
                <Skeleton className="!h-full w-full" />
            </div>
        );
    }
    if (empty) {
        return (
            <div
                className={clsx('flex items-center justify-center rounded-md bg-sunken px-4 text-center text-small text-muted', className)}
                style={{ height }}
            >
                {t('empty')}
            </div>
        );
    }
    return <>{children}</>;
};

export interface TooltipRow {
    key: string;
    label?: string;
    value: string;
    swatch?: { uid: string; i: number };
}

/** Floating details box, placed at chart-pixel x and kept inside `width`. Hidden from AT (the live region and table cover it). */
export const Tooltip = ({ x, y = 4, width, title, rows }: { x: number; y?: number; width: number; title: string; rows: TooltipRow[] }) => (
    <div
        aria-hidden="true"
        className="pointer-events-none absolute z-10 max-w-[70%] rounded-md border px-3 py-2 text-small"
        style={{
            left: x,
            top: y,
            transform: x > width / 2 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
            background: SURFACE,
            color: 'var(--text-body, #333333)',
            borderColor: 'var(--border-hairline, rgb(28 28 28 / 0.10))',
            boxShadow: 'var(--shadow-e2, 0 4px 12px rgb(0 0 0 / 0.12))',
        }}
    >
        <p className="font-medium" style={{ color: 'var(--text-strong, #1c1c1c)' }}>
            {title}
        </p>
        {rows.map((r) => (
            <p key={r.key} className="flex items-center gap-2 tabular-nums">
                {r.swatch && <Swatch uid={r.swatch.uid} i={r.swatch.i} />}
                {r.label && <span>{r.label}</span>}
                <span style={{ color: 'var(--text-strong, #1c1c1c)' }}>{r.value}</span>
            </p>
        ))}
    </div>
);

